/**
 * LoginPage -- Login and Register form.
 *
 * Toggle between login / register modes.  Uses useAuthStore (ADR-021)
 * and the Input + Button UI primitives (ADR-022).
 */
import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore, useAuthActions } from '../state/useAuthStore';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { logger } from '../services/logger.client';

const log = logger.child({ module: 'LoginPage' });

type Mode = 'login' | 'register';

export function LoginPage() {
  const token = useAuthStore((s) => s.token);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const { login, register, clearError } = useAuthActions();

  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  /* If already authenticated, redirect to dashboard */
  if (token) {
    return <Navigate to="/" replace />;
  }

  function toggleMode() {
    setMode((prev) => (prev === 'login' ? 'register' : 'login'));
    clearError();
    log.debug('Toggled auth mode', { mode: mode === 'login' ? 'register' : 'login' });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (mode === 'login') {
      await login({ email, password });
    } else {
      await register({ email, password, confirmPassword, name });
    }
  }

  const heading = mode === 'login' ? 'Sign In' : 'Create Account';
  const toggleLabel =
    mode === 'login'
      ? "Don't have an account? Register"
      : 'Already have an account? Sign in';

  return (
    <div className="login-page">
      <div className="login-card">
        <h1 className="login-card__title">{heading}</h1>
        <p className="login-card__subtitle">MyManyResumes</p>

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          {mode === 'register' && (
            <Input
              label="Full Name"
              id="name"
              name="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              autoComplete="name"
              required
            />
          )}

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />

          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="********"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            required
          />

          {mode === 'register' && (
            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="********"
              autoComplete="new-password"
              required
            />
          )}

          {error && (
            <div className="login-form__error" role="alert">
              {error}
            </div>
          )}

          <Button type="submit" variant="primary" size="lg" loading={isLoading}>
            {mode === 'login' ? 'Sign In' : 'Register'}
          </Button>
        </form>

        <button type="button" className="login-card__toggle" onClick={toggleMode}>
          {toggleLabel}
        </button>
      </div>
    </div>
  );
}
